/**
 * CreatorProfileCard — a discreet Dashboard tile that surfaces the values
 * captured during onboarding (content_type, platforms, goal, cadence).
 *
 * The card is purely visual — it only reads /account/profile and provides a
 * "Modifier" shortcut back to the onboarding flow. If the user hasn't
 * completed onboarding yet, the card renders nothing (no fake data).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useT } from '@/lib/i18n';
import { Pencil, Sparkles } from 'lucide-react';

const CONTENT_LABEL_FR = {
  podcast: 'Podcast', gaming: 'Gaming', business: 'Business', finance: 'Finance',
  education: 'Éducation', fitness: 'Fitness', lifestyle: 'Lifestyle',
  vlog: 'Vlogs', other: 'Autre',
};
const CONTENT_LABEL_EN = {
  podcast: 'Podcast', gaming: 'Gaming', business: 'Business', finance: 'Finance',
  education: 'Education', fitness: 'Fitness', lifestyle: 'Lifestyle',
  vlog: 'Vlogs', other: 'Other',
};

const PLATFORM_LABEL = {
  tiktok: 'TikTok', youtube_shorts: 'YouTube Shorts', instagram_reels: 'Instagram Reels',
  linkedin: 'LinkedIn', x: 'X',
};

const GOAL_LABEL_FR = {
  views: 'Plus de vues', subs: 'Plus d\'abonnés', clients: 'Trouver des clients',
  time: 'Gagner du temps', brand: 'Développer ma marque',
};
const GOAL_LABEL_EN = {
  views: 'More views', subs: 'More subscribers', clients: 'Find clients',
  time: 'Save time', brand: 'Build brand',
};

export const CreatorProfileCard = () => {
  const { lang } = useT();
  const [profile, setProfile] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get('/account/profile')
      .then((r) => setProfile(r.data && r.data.completed ? r.data : null))
      .catch(() => setProfile(null))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded || !profile) return null;

  const contentLabel = lang === 'fr' ? CONTENT_LABEL_FR : CONTENT_LABEL_EN;
  const goalLabel = lang === 'fr' ? GOAL_LABEL_FR : GOAL_LABEL_EN;

  const rows = [
    { key: 'content', label: lang === 'fr' ? 'Type' : 'Type', value: profile.content_type ? contentLabel[profile.content_type] : '—' },
    {
      key: 'platforms',
      label: lang === 'fr' ? 'Plateformes' : 'Platforms',
      value: (profile.platforms || []).length
        ? profile.platforms.map((p) => PLATFORM_LABEL[p] || p).join(', ')
        : '—',
    },
    { key: 'goal', label: lang === 'fr' ? 'Objectif' : 'Goal', value: profile.goal ? goalLabel[profile.goal] : '—' },
    {
      key: 'cadence',
      label: lang === 'fr' ? 'Cadence' : 'Cadence',
      value: profile.cadence
        ? `${profile.cadence} ${lang === 'fr' ? 'vidéos/sem.' : 'videos/wk'}`
        : '—',
    },
  ];

  return (
    <div
      className="rounded-2xl border border-white/8 bg-gradient-to-br from-white/[0.03] to-transparent p-5 hover:border-white/15 transition-colors"
      data-testid="creator-profile-card"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-[#D4FF00]/10 border border-[#D4FF00]/20 flex items-center justify-center">
            <Sparkles className="w-3 h-3 text-[#D4FF00]" />
          </div>
          <h3 className="text-xs uppercase tracking-[0.18em] text-zinc-400 font-mono">
            {lang === 'fr' ? 'Ton profil' : 'Your profile'}
          </h3>
        </div>
        <Link
          to="/onboarding"
          className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-white transition group"
          data-testid="creator-profile-edit"
          aria-label={lang === 'fr' ? 'Modifier ton profil créateur' : 'Edit your creator profile'}
        >
          <Pencil className="w-3 h-3 group-hover:text-[#D4FF00] transition" />
          {lang === 'fr' ? 'Modifier' : 'Edit'}
        </Link>
      </div>

      <dl className="space-y-3" data-testid="creator-profile-list">
        {rows.map((r) => (
          <div key={r.key} className="flex items-baseline justify-between gap-3 text-sm">
            <dt className="text-zinc-500 shrink-0 text-xs uppercase tracking-wider font-mono">{r.label}</dt>
            <dd
              className="text-zinc-100 text-right font-medium truncate"
              data-testid={`creator-profile-${r.key}`}
              title={r.value}
            >
              {r.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

export default CreatorProfileCard;
